/**
 * "Open POS" — walk the signed-in SaaS user into the Laravel POS for a business.
 *
 * Exchanges the SaaS session for a short-lived, single-use POS-handoff token
 * (POST /pos/handoff), then opens the POS's single-use exchange endpoint
 * (GET <pos-base>/sso/exchange?token=…&return_to=…) in a NEW TAB, which verifies
 * the token, logs the user in, and lands them on returnTo.
 *
 * The POS base URL is per-reseller-domain (resolved server-side). Until that or
 * VITE_POS_BASE is configured it can't navigate, so it surfaces a notice.
 */
import { USE_MOCKS, apiPost } from './client';

const POS_BASE_FALLBACK = (import.meta.env.VITE_POS_BASE ?? '').replace(/\/$/, '');

type Handoff = { token: string; posBaseUrl?: string | null };

/** Build the POS single-use exchange URL, or null if no base is configured. */
function posExchangeUrl(token: string, returnTo: string, posBaseUrl?: string | null): string | null {
  const base = (posBaseUrl ?? '').replace(/\/$/, '') || POS_BASE_FALLBACK;
  if (!base) return null;
  return `${base}/sso/exchange?token=${encodeURIComponent(token)}&return_to=${encodeURIComponent(returnTo)}`;
}

/**
 * Open the POS in a NEW TAB. The tab is opened synchronously inside the click
 * gesture (BEFORE the awaited handoff) so the browser doesn't popup-block it;
 * once the single-use token resolves we point the tab at the POS exchange
 * endpoint. If the popup was blocked we fall back to a same-tab navigation so
 * "Open POS" still works.
 */
async function handoffToPos(fetchToken: () => Promise<Handoff>, returnTo: string): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  if (tab) {
    // Brief static placeholder via DOM methods (no document.write). Static text only.
    try {
      tab.document.title = 'Opening POS…';
      const p = tab.document.createElement('p');
      p.textContent = 'Opening your POS…';
      p.style.cssText = 'font-family:system-ui,-apple-system,Segoe UI,sans-serif;padding:2rem;color:#3f3f46';
      tab.document.body?.appendChild(p);
    } catch {
      /* edge cases — the tab will navigate to the POS momentarily regardless */
    }
  }
  try {
    const { token, posBaseUrl } = await fetchToken();
    const url = posExchangeUrl(token, returnTo, posBaseUrl);
    if (!url) {
      tab?.close();
      window.alert('POS base URL is not configured for this business yet (no primary domain).');
      return;
    }
    if (tab) tab.location.href = url;
    else window.location.href = url; // popup blocked → same-tab fallback
  } catch (e) {
    tab?.close();
    // Surface the server's refusal (e.g. "trial ended — pay the first invoice")
    // instead of dying as an unhandled rejection behind a blank tab.
    window.alert(e instanceof Error && e.message ? e.message : 'Could not open the POS.');
  }
}

/** Self-service "Open POS" — the signed-in owner/staff walks into their own POS (new tab). */
export async function openPos(businessId: string | undefined, returnTo = '/home'): Promise<void> {
  if (!businessId) return;
  if (USE_MOCKS) {
    window.alert('Open POS — in real mode this SSO-hands you into the Laravel POS for this business.');
    return;
  }
  await handoffToPos(() => apiPost<Handoff>('/pos/handoff', { businessId }), returnTo);
}

/**
 * Admin support "Open POS" — an audited, read-only-flagged handoff that signs the
 * platform admin into a business's POS AS the business owner (the only identity
 * Laravel can resolve into that POS). The server writes a `pos.impersonation.start`
 * audit row; the token carries `ro`+`imp`. Platform-only on the server. New tab.
 */
export async function openBusinessPosAsAdmin(businessId: string | undefined, returnTo = '/home'): Promise<void> {
  if (!businessId) return;
  if (USE_MOCKS) {
    window.alert('Open POS (admin) — in real mode this audited, read-only-flagged handoff signs you into the business POS as the owner for support.');
    return;
  }
  await handoffToPos(() => apiPost<Handoff>(`/admin/businesses/${businessId}/open-pos`, {}), returnTo);
}
